import { useState, useEffect, useMemo } from 'react';
import { Modal as AntModal, Tabs } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { usePost, useGet, useAuth } from 'hooks';
import { getAlertToCreate } from 'selectors/alerts';
import { setAlertToCreate } from 'actions/alerts';
import { sanityCheckAlert, allUsersIncludeInactive } from 'services/api';
import { searchOrigin } from 'components/Search/Results/constants';
import licenseType from 'models/licenseType';
import CreateSearch from './CreateSearch';
import EditSearch from './EditSearch';
import usePermissions from './usePermissions';
import { getListsFilters } from './helpers';

const CreateModal = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const activeAlert = useSelector(getAlertToCreate);
  const [activeTab, setActiveTab] = useState('create');
  const { user } = useAuth();

  const [
    {
      res: recentItemsResults,
      loading: sanityCheckLoading,
      error: sanityError,
    },
    getUserAlerts,
  ] = usePost({
    url: sanityCheckAlert,
  });

  const [{ res: allUsersRes, loading: allUsersLoading }] = useGet({
    url: allUsersIncludeInactive,
    onMount: true,
  });

  const userOptions = useMemo(
    () =>
      allUsersRes?.userAccounts
        .filter(u => u.active && u.legacyUserId !== user.userId)
        .map(u => ({
          fullname: u.fullname,
          licenses: u.licenses,
          userId: u.legacyUserId,
          missingPurchasingAlertLicense:
            activeAlert.searchOrigin === searchOrigin.purchasingSearch &&
            !u.licenses.some(l => l.type === licenseType.academic),
        }))
        .sort((a, b) => a.fullname.localeCompare(b.fullname)) || [],
    [allUsersRes, user.userId, activeAlert.searchOrigin]
  );

  useEffect(() => {
    if (activeAlert) {
      getUserAlerts({
        ...activeAlert,
        searchFilters: {
          ...getListsFilters(activeAlert, x => x),
        },
        nestedQueries: activeAlert.nestedQueries,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    dispatch(setAlertToCreate());
  };

  const { checkUserPermissions } = usePermissions({
    activeAlert,
    userLicenses: user.licenses,
    allUsersRes,
  });

  if (!activeAlert) return null;

  return (
    <AntModal
      open
      title={user.disableExport ? 'Save Searches' : 'Save Searches & Alerts'}
      onCancel={handleCancel}
      footer={null}
      bodyStyle={{ padding: '15px' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            label: 'Save New Search',
            key: 'create',
            children: (
              <CreateSearch
                sanityCheckLoading={sanityCheckLoading}
                sanityError={sanityError}
                recentItemsResults={recentItemsResults}
                allUsersLoading={allUsersLoading}
                onSuccess={onSuccess}
                userOptions={userOptions}
                checkUserPermissions={checkUserPermissions}
              />
            ),
          },
          {
            label: 'Update Existing Search',
            key: 'update',
            children: (
              <EditSearch
                sanityCheckLoading={sanityCheckLoading}
                sanityError={sanityError}
                recentItemsResults={recentItemsResults}
                allUsersLoading={allUsersLoading}
                userAccounts={allUsersRes?.userAccounts || []}
                userOptions={userOptions}
                checkUserPermissions={checkUserPermissions}
                onSuccess={onSuccess}
              />
            ),
          },
        ]}
      />
    </AntModal>
  );
};

export default CreateModal;
